import {
  ASSIGN_TO_FILE,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  Colors,
  LoadingIndicator,
  SaveFormTop,
  SearchInput,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import '../../shared/style/reactTabStyle.css';
import { EditFileAddonsList } from './EditFileAddonsList';
import { EditFileProductsList } from './EditFileProductsList';

const GET_MEDIA_ASSIGNMENTS = `
  query GetMediaAssignments($mediaId: String!) {
    media {
      findMediaAssignments(mediaId: $mediaId) {
        products
        addons
      }
    }
  }
`;

type Props = {
  fileId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'products' | 'addons';
};

export const FileAssignmentForm = ({
  fileId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const listMaxHeight = height - 50;
  const currentWorkspace = useCurrentWorkspace();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;

  const [originalProductAssignments, setOriginalProductAssignments] = useState<
    string[] | undefined
  >();
  const [productAssignments, setProductAssignments] = useState<string[]>();
  const [originalAddonAssignments, setOriginalAddonAssignments] = useState<
    string[] | undefined
  >();
  const [addonAssignments, setAddonAssignments] = useState<string[]>();
  const [tabIndex, setTabIndex] = useState(initialTab === 'addons' ? 1 : 0);
  const [keyword, setKeyword] = useState('');

  const { data: assignmentsData, isPending: assignmentsLoading } =
    useCustomQuery({
      query: GET_MEDIA_ASSIGNMENTS,
      cacheKey: ['media', 'assignments', fileId],
      variables: { mediaId: fileId },
    });

  useEffect(() => {
    const existingProducts =
      assignmentsData?.media?.findMediaAssignments?.products || [];
    const existingAddons =
      assignmentsData?.media?.findMediaAssignments?.addons || [];
    setOriginalProductAssignments(existingProducts);
    setProductAssignments(existingProducts);
    setOriginalAddonAssignments(existingAddons);
    setAddonAssignments(existingAddons);
  }, [assignmentsData, fileId]);

  const { mutateAsync: assignToFile } = useCustomMutation({
    query: ASSIGN_TO_FILE,
  });

  const { data: mediaData } = useCustomQuery({
    query: GET_MEDIA_ASSIGNMENTS,
    cacheKey: ['media', 'assignments', fileId],
    variables: { mediaId: fileId },
  });

  useEffect(() => {
    const existingProducts =
      mediaData?.media?.findMediaAssignments?.products || [];
    const existingAddons = mediaData?.media?.findMediaAssignments?.addons || [];
    setOriginalProductAssignments(existingProducts);
    setProductAssignments(existingProducts);
    setOriginalAddonAssignments(existingAddons);
    setAddonAssignments(existingAddons);
  }, [fileId, mediaData]);

  useEffect(() => {
    if (initialTab) setTabIndex(initialTab === 'addons' ? 1 : 0);
  }, [initialTab, fileId]);

  useEffect(() => {
    setKeyword('');
  }, [tabIndex]);

  const handleSubmit = async () => {
    if (!currentWorkspace?.id) return;
    const inputBase = {
      mediaId: fileId,
      workspaceId: currentWorkspace.id,
    } as any;

    if (tabIndex === 0) {
      await assignToFile({
        input: { ...inputBase, productIds: productAssignments },
      } as any);
    } else {
      await assignToFile({
        input: { ...inputBase, addonIds: addonAssignments },
      } as any);
    }

    queryClient.invalidateQueries({ queryKey: ['media'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['product'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['addon'], exact: false });
    onComplete();
  };

  const isProductsDirty = () =>
    productAssignments &&
    originalProductAssignments &&
    !(
      originalProductAssignments.length === productAssignments.length &&
      originalProductAssignments.every((id) =>
        productAssignments.includes(id)
      ) &&
      productAssignments.every((id) => originalProductAssignments.includes(id))
    );
  const isAddonsDirty = () =>
    addonAssignments &&
    originalAddonAssignments &&
    !(
      originalAddonAssignments.length === addonAssignments.length &&
      originalAddonAssignments.every((id) => addonAssignments.includes(id)) &&
      addonAssignments.every((id) => originalAddonAssignments.includes(id))
    );
  const isDirty = () => (tabIndex === 0 ? isProductsDirty() : isAddonsDirty());

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      <View style={styles.container}>
        {assignmentsLoading ? (
          <LoadingIndicator flexContainer />
        ) : (
          <View style={{ flex: 1 }}>
            <SearchInput
              value={keyword}
              onChangeText={setKeyword}
              onDebounce={setKeyword}
              debounceLength={3}
              backgroundColor={Colors.white}
              placeholder={
                tabIndex === 0
                  ? t('file.assignments.productsSearchPlaceholder')
                  : t('file.assignments.addonsSearchPlaceholder')
              }
              height={44}
            />
            <View style={styles.assignmentContainer} onLayout={onLayout}>
              <Tabs selectedIndex={tabIndex} onSelect={(i) => setTabIndex(i)}>
                <TabList>
                  <Tab>{t('file.tabs.products')}</Tab>
                  {isOEM && <Tab>{t('file.tabs.addons')}</Tab>}
                </TabList>
                <View style={styles.listContainer}>
                  <TabPanel>
                    {currentWorkspace?.id && productAssignments && (
                      <EditFileProductsList
                        workspaceId={currentWorkspace.id}
                        fileId={fileId}
                        maxHeight={listMaxHeight}
                        keyword={keyword}
                        assignments={productAssignments}
                        updateAssignments={setProductAssignments}
                      />
                    )}
                  </TabPanel>
                  {isOEM && (
                    <TabPanel>
                      {currentWorkspace?.id && addonAssignments && (
                        <EditFileAddonsList
                          workspaceId={currentWorkspace.id}
                          fileId={fileId}
                          maxHeight={listMaxHeight}
                          keyword={keyword}
                          assignments={addonAssignments}
                          updateAssignments={setAddonAssignments}
                        />
                      )}
                    </TabPanel>
                  )}
                </View>
              </Tabs>
            </View>
          </View>
        )}
      </View>
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 6,
  },
  rowText: { fontSize: 16, fontWeight: '500' },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
});
